/**
 * Turn any API error into a string that is safe to render.
 *
 * FastAPI returns `detail` as a plain string for HTTPExceptions, but as an
 * array of `{ loc, msg, type }` objects for request validation errors (422).
 * Passing that array straight to a toast or `setError` makes React throw
 * "Objects are not valid as a React child" and takes the whole page down,
 * which is how a clinician lost an unsaved bio in September 2026.
 */
interface ValidationItem {
  loc?: Array<string | number>;
  msg?: string;
}

const FIELD_LABELS: Record<string, string> = {
  profile_bio: 'Bio',
  website_url: 'Website',
  video_intro_url: 'Intro video',
  first_name: 'First name',
  last_name: 'Last name',
  license_number: 'License number',
  duration_minutes: 'Duration',
  price: 'Price',
  description: 'Description',
  name: 'Name',
  phone: 'Phone',
  specialty: 'Professional title',
  specialties: 'Areas of focus',
};

const fieldLabel = (loc?: Array<string | number>): string | null => {
  if (!loc || loc.length === 0) return null;
  const last = [...loc].reverse().find((part) => typeof part === 'string' && part !== 'body');
  if (typeof last !== 'string') return null;
  return FIELD_LABELS[last] || last.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

const itemMessage = (item: ValidationItem | string): string | null => {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return null;
  const msg = typeof item.msg === 'string' ? item.msg.replace(/^Value error, /, '') : null;
  if (!msg) return null;
  const label = fieldLabel(item.loc);
  return label ? `${label}: ${msg}` : msg;
};

/** Extract a human-readable message from an axios error (or anything else). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as any)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map(itemMessage).filter((m): m is string => !!m);
    if (parts.length) return parts.join('. ');
  }
  if (detail && typeof detail === 'object') {
    const single = itemMessage(detail as ValidationItem);
    if (single) return single;
  }
  return fallback;
}
